export interface LoadingVisibilityOptions {
  showDelayMs?: number;
  minVisibleMs?: number;
}

const DEFAULT_SHOW_DELAY_MS = 200;
const DEFAULT_MIN_VISIBLE_MS = 400;

export class LoadingVisibilityController {
  visible = false;

  private activeRequests = 0;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private visibleSince = 0;

  private readonly showDelayMs: number;
  private readonly minVisibleMs: number;

  constructor(
    private readonly onVisibilityChange: (visible: boolean) => void,
    options: LoadingVisibilityOptions = {}
  ) {
    this.showDelayMs = options.showDelayMs ?? DEFAULT_SHOW_DELAY_MS;
    this.minVisibleMs = options.minVisibleMs ?? DEFAULT_MIN_VISIBLE_MS;
  }

  start(): void {
    this.activeRequests += 1;
    this.clearHideTimer();

    if (this.visible || this.showTimer) return;

    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      if (this.activeRequests === 0 || this.visible) return;

      this.visible = true;
      this.visibleSince = Date.now();
      this.onVisibilityChange(true);
    }, this.showDelayMs);
  }

  stop(): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    if (this.activeRequests > 0) return;

    this.clearShowTimer();

    if (!this.visible) return;

    const elapsed = Date.now() - this.visibleSince;
    const remaining = this.minVisibleMs - elapsed;

    if (remaining <= 0) {
      this.hide();
      return;
    }

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.hide();
    }, remaining);
  }

  destroy(): void {
    this.activeRequests = 0;
    this.clearShowTimer();
    this.clearHideTimer();

    if (this.visible) {
      this.hide();
    }
  }

  private hide(): void {
    if (!this.visible) return;

    this.visible = false;
    this.visibleSince = 0;
    this.onVisibilityChange(false);
  }

  private clearShowTimer(): void {
    if (!this.showTimer) return;

    clearTimeout(this.showTimer);
    this.showTimer = null;
  }

  private clearHideTimer(): void {
    if (!this.hideTimer) return;

    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }
}
